import axios from 'axios';
import {
  flow,
  getEnv,
  getRoot,
  resolveIdentifier,
  types,
} from 'mobx-state-tree';

import globalUserMessages from '../i18n/globalUserMessages';
import Product from '../models/Product';
import StatefulStore from '../models/StatefulStore';
import Modal from '../models/modal/Modal';
import OrderCheckout from '../models/order/checkout/OrderCheckout';
import RequestState, { RequestStateType } from '../types/RequestState';
import CheckoutPageModule from '../types/checkout/CheckoutPageModule';
import { globalUserMessageDataKey } from '../util/constants';
import { blockScroll, unblockScroll } from '../util/dom';

const CheckoutCartModal = Modal.named('CheckoutCartModal', {
  selectedProduct: types.maybeNull(types.reference(Product)),
  position: types.maybeNull(types.integer),
}).actions((self) => {
  return {
    setup: (productId, position) => {
      self.selectedProduct = resolveIdentifier(
        Product,
        getRoot(self).productStore.products,
        productId
      );
      self.position = position;
    },
    close: () => {
      unblockScroll();
      self.isOpen = false;
    },
    open: () => {
      blockScroll();
      self.isOpen = true;
    },
  };
});

const CheckoutStore = StatefulStore.named('CheckoutStore')
  .props({
    order: types.maybeNull(OrderCheckout),
    modulesState: types.optional(types.map(RequestStateType), {}),
    checkoutCartModal: types.optional(CheckoutCartModal, {
      isOpen: false,
    }),
    registrationStatus: types.maybeNull(
      types.model('RegistrationStatus', {
        messages: types.optional(types.array(types.string), []),
        status: types.string,
        success: types.boolean,
      })
    ),
    scrollToAlertCounter: types.optional(types.number, 0),
  })
  .actions((self) => {
    let source;
    let registrationAlertTimeout;

    const convertItemsToMap = (items) => {
      return items.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    };

    const transformPayload = (additionalProduct) => {
      return {
        customer_fields: self.order.customer_fields,
        extra_fields: self.order.extra_fields,
        products: self.order.productList,
        additional_product: additionalProduct,
        selected_payment: self.order.selectedPayment,
        selected_shipping: self.order.selectedShipping,
      };
    };

    const updateOrderFields = (order) => {
      // Preserve pickup points if they've been already fetched
      const pickupPoints = self.order.pickupPoints;
      const pickupPointState = self.order.pickupPointState;

      self.order = {
        ...order,
        pickupPoints,
        pickupPointState,
      };

      self.order.customer_fields.replace(
        convertItemsToMap(order.customer_fields)
      );
      self.order.extra_fields.replace(convertItemsToMap(order.extra_fields));
    };

    return {
      addProductToOrder: flow(function* addProductToOrder({
        product,
        quantity = 1,
        activeProductId,
      }) {
        const additionalProduct = {
          id: activeProductId || product.id,
          quantity,
          is_free_of_charge: product.is_free_of_charge || false,
          category_id: product.main_category_id,
        };

        yield self.validateOrder({
          module: CheckoutPageModule.CART_MODULE,
          additionalProduct,
        });
      }),
      registerCustomer: flow(function* registerCustomer() {
        const module = 'REGISTRATION';
        self.modulesState.set(module, RequestState.LOADING);

        const payload = {
          productListProducts: self.order.productList,
        };

        try {
          yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`checkout/register-customer`, payload);

          self.modulesState.set(module, RequestState.LOADED);
          window.location.reload();
        } catch (error) {
          self.modulesState.set(module, RequestState.ERROR);
          self.registrationStatus = error.response?.data?.messages
            ? error.response?.data
            : null;
          registrationAlertTimeout = setTimeout(() => {
            self.resetRegistrationAlert();
          }, 5000);
          throw error;
        }
      }),
      resetRegistrationAlert: () => {
        self.registrationStatus = null;
        clearTimeout(registrationAlertTimeout);
        registrationAlertTimeout = null;
        self.modulesState.set('REGISTRATION', RequestState.NONE);
      },
      loadOrder: flow(function* loadOrder() {
        self.setLoading(true);

        try {
          const timestamp = new Date().getTime();
          const order = yield getEnv(self).apiWrapper.request(`checkout`, {
            params: {
              t: timestamp,
            },
          });

          self.order = order;
        } catch (error) {
          if (error.response?.status === 403) {
            self.dispatch403Message();
          } else {
            self.setError(error);
          }
          throw error;
        }
        self.setLoading(false);
      }),
      confirmOrder: flow(function* confirmOrder(module, additionalProduct) {
        try {
          self.modulesState.set(module, RequestState.LOADING);

          const payload = transformPayload(additionalProduct);

          const response = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`checkout/confirm`, payload);

          updateOrderFields(response.data);
          self.modulesState.set(module, RequestState.LOADED);
        } catch (error) {
          if (error.response?.status === 403) {
            self.dispatch403Message();
          } else {
            // Update order to show backend error alerts.
            updateOrderFields(error.response.data);
            self.scrollToAlertCounter++;
          }
          self.modulesState.set(module, RequestState.ERROR);
          throw error;
        }
      }),
      updateOrder: flow(function* updateOrder({ module, additionalProduct }) {
        if (self.order.productList.length === 0) {
          return;
        }
        self.cancelOrder();

        self.modulesState.set(module, RequestState.LOADING);

        const payload = transformPayload(additionalProduct);

        try {
          const response = yield getEnv(self)
            .apiWrapper.apiAxios()
            .put(`checkout`, payload, {
              cancelToken: source.token,
            });

          updateOrderFields(response.data);
          self.resetRegistrationAlert();
          yield self.validateShipping();
          self.modulesState.set(module, RequestState.LOADED);
        } catch (error) {
          if (error.response?.status === 403) {
            self.dispatch403Message();
          } else {
            // We do not set error when cancel occurs as the cancel is caused
            // by another API query that will handle the state properly.
            if (!axios.isCancel(error)) {
              throw error;
            }
          }
          self.modulesState.set(module, RequestState.ERROR);
        }
      }),
      cancelOrder: () => {
        if (source) {
          source.cancel();
        }

        source = axios.CancelToken.source();
      },
      dispatch403Message: () => {
        const content = {
          type: globalUserMessageDataKey.globalUserMessage,
          detail: {
            forceRefresh: true,
            ...globalUserMessages.pageExpired,
          },
        };
        self.dispatchEvent(content);
      },
      validateOrder: flow(function* validateOrder(args) {
        //yield self.validateShipping();
        yield self.updateOrder(args);
      }),
      validateShipping: flow(function* validateShipping() {
        if (self.order.cartIsEmpty) {
          return;
        }

        const pickupPointIsRequired =
          self.order.selected_shipping?.module?.requires_pickup_point || null;
        const noPickupPoints =
          self.order.currentPickupPointState !== RequestState.LOADED;

        if (pickupPointIsRequired && noPickupPoints) {
          yield self.order.fetchPickupPoints();
        }
      }),
      openProductPageModal(productId, position) {
        self.checkoutCartModal.setup(productId, position);
        self.checkoutCartModal.toggle(true, true);
      },
    };
  })
  .views((self) => ({
    get cartIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.CART_MODULE) ===
        RequestState.LOADING
      );
    },
    get checkoutIsLoading() {
      return [...self.modulesState.values()].some(
        (state) => state === RequestState.LOADING
      );
    },
    get customerInfoIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.CUSTOMER_INFO_MODULE) ===
          RequestState.LOADING || self.state === RequestState.LOADING
      );
    },
    get shippingInfoIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.SHIPPING_INFO_MODULE) ===
          RequestState.LOADING || self.state === RequestState.LOADING
      );
    },
    get paymentMethodIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.PAYMENT_METHOD_MODULE) ===
        RequestState.LOADING
      );
    },
    get registrationMessages() {
      return self.registrationStatus && self.registrationStatus.messages;
    },
    get shippingMethodIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.SHIPPING_METHOD_MODULE) ===
        RequestState.LOADING
      );
    },
    get summaryIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.SUMMARY_MODULE) ===
        RequestState.LOADING
      );
    },
    get orderTotals() {
      return self.order.totals ? [...self.order.totals.values()] : [];
    },
  }));

export default CheckoutStore;
